import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";

import { UserContext } from "../contexts";
import "./move-transaction-modal.scss";

/**
 * "Move Transaction" — re-file a whole deal under a different store.
 *
 * Two steps on purpose. Step 1 picks the store; step 2 shows what will actually
 * happen and is the only place the move can be confirmed.
 *
 * The step-2 wording is NOT written here. It is rendered from
 * GET /requests/:id/move-transaction/preview, so the sentence a user agrees to
 * is generated from their own deal by the same service that performs the move.
 * Hand-written copy would drift from the behaviour the moment either changed —
 * and this dialog is the last thing standing between a click and a sale changing
 * company.
 *
 * `xero.failures` in the POST response means the deal moved but an invoice could
 * not be voided in the source organisation. The modal stays open in that case:
 * that invoice is still live, which is duplicated revenue until someone voids
 * it, so it must never close as a plain success. Same rule as
 * ChangeDealDateModal.
 */
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The rows worth naming in the summary; the rest are noise for a human. */
const COUNT_LABELS = [
  ["orders", "order"],
  ["requests", "request"],
  ["order_items", "item"],
  ["payments", "payment"],
  ["quotes", "quote"],
  ["listings", "listing"],
  ["deposits", "deposit"],
  ["commissions", "commission"],
];

const MoveTransactionModal = ({ requestId, onClose, onMoved }) => {
  const { accessibleStores, activeStoreId } = useContext(UserContext) || {};
  const [targetId, setTargetId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState(null);
  const [xeroFailures, setXeroFailures] = useState([]);

  // "Master Store" is a sentinel with a null id, not a real store — a deal
  // cannot be filed into it. The store the deal is already in is excluded by
  // the preview's own blocker, but hiding it avoids offering a dead end.
  const stores = useMemo(
    () =>
      (accessibleStores || []).filter(
        (s) => s && s.id != null && s.id !== activeStoreId
      ),
    [accessibleStores, activeStoreId]
  );

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`/api/requests/${requestId}/move-transaction/preview`, {
        params: { store_id: targetId },
      })
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              "Couldn’t work out what this move would do. Please try again."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetId, requestId]);

  const confirm = async () => {
    setMoving(true);
    setError(null);
    setXeroFailures([]);
    try {
      const { data } = await axios.post(
        `/api/requests/${requestId}/move-transaction`,
        { store_id: targetId }
      );
      const failures = data?.xero?.failures || [];
      if (failures.length) {
        setXeroFailures(failures);
        setMoving(false);
        return;
      }
      onMoved?.(preview?.target?.name || "the other store");
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Couldn’t move this transaction. Nothing was changed."
      );
      setMoving(false);
    }
  };

  const blockers = preview?.blockers || [];
  const counts = preview?.counts || {};
  const movedRows = COUNT_LABELS.filter(([key]) => (counts[key] || 0) > 0).map(
    ([key, word]) => plural(counts[key], word)
  );

  return createPortal(
    <div className="mtm__overlay" onMouseDown={onClose}>
      <div
        className="mtm"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mtm__header">
          <h3>{targetId ? "Confirm move" : "Move Transaction"}</h3>
          <button
            type="button"
            className="mtm__close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="mtm__body">
          {!targetId && (
            <>
              <p className="mtm__hint">
                Choose the store this transaction should belong to. Nothing moves
                until you confirm on the next step.
              </p>
              {!stores.length && (
                <div className="mtm__error">
                  You only have access to one store, so there is nowhere to move
                  this to.
                </div>
              )}
              <ul className="mtm__stores">
                {stores.map((store) => (
                  <li key={store.id}>
                    <button
                      type="button"
                      className="mtm__store"
                      onClick={() => setTargetId(store.id)}
                    >
                      <span className="mtm__store-name">{store.name}</span>
                      <span className="mtm__store-go" aria-hidden>
                        →
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {targetId && loading && (
            <div className="mtm__loading">Working out what will move…</div>
          )}

          {targetId && !loading && error && (
            <div className="mtm__error">{error}</div>
          )}

          {targetId && !loading && preview && (
            <>
              {!!blockers.length && (
                <div className="mtm__error">
                  <strong>This transaction can’t be moved:</strong>
                  <ul>
                    {blockers.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              {!blockers.length && (
                <>
                  <p className="mtm__lead">
                    Moving this transaction from{" "}
                    <strong>{preview.source?.name}</strong> to{" "}
                    <strong>{preview.target?.name}</strong>.
                  </p>

                  <ul className="mtm__facts">
                    <li>
                      <strong>{movedRows.join(", ")}</strong> will move to{" "}
                      {preview.target?.name}.
                    </li>

                    {(preview.clients || []).map((client) => (
                      <li key={client.user_id}>
                        {client.action === "copy" && (
                          <>
                            The client <strong>{client.name}</strong> is{" "}
                            <strong>copied</strong>, not moved — their history in{" "}
                            {preview.source?.name} stays there.
                          </>
                        )}
                        {client.action === "reuse_copy" && (
                          <>
                            <strong>{client.name}</strong> already exists in{" "}
                            {preview.target?.name}, so that record is reused —
                            no duplicate is created.
                          </>
                        )}
                        {client.action === "already_there" && (
                          <>
                            <strong>{client.name}</strong> already belongs to{" "}
                            {preview.target?.name}.
                          </>
                        )}
                        {client.action === "left_alone" && (
                          <>
                            <strong>{client.name}</strong> is a staff account and
                            is left as-is.
                          </>
                        )}
                      </li>
                    ))}

                    {(preview.xero?.documents || []).map((doc) => (
                      <li key={doc.order_id}>
                        {doc.action === "auto_void" ? (
                          <>
                            Invoice <strong>{doc.invoice_number}</strong> will be{" "}
                            <strong>voided</strong> in {preview.source?.name}’s
                            Xero organisation. Raise a new one here afterwards.
                          </>
                        ) : (
                          <>
                            Invoice <strong>{doc.invoice_number}</strong> is{" "}
                            {doc.reason === "paid"
                              ? "already paid"
                              : "not linked to a Xero organisation"}
                            , so it is <strong>left untouched</strong>. Void it
                            yourself in {preview.source?.name}’s Xero
                            organisation
                            {doc.reason === "paid"
                              ? " after removing its payment"
                              : ""}
                            , then raise a new one here.
                          </>
                        )}
                      </li>
                    ))}

                    {!(preview.xero?.documents || []).length && (
                      <li>There is no Xero invoice on this transaction yet.</li>
                    )}

                    {preview.xero?.note && <li>{preview.xero.note}</li>}
                  </ul>

                  <p className="mtm__warn">
                    This changes which store’s books this sale belongs to. It can
                    only be undone by moving it back.
                  </p>
                </>
              )}
            </>
          )}

          {!!xeroFailures.length && (
            <div className="mtm__warning">
              <strong>
                The transaction moved, but Xero was not fully updated:
              </strong>
              <ul>
                {xeroFailures.map((f, i) => (
                  <li key={i}>
                    {f.invoice_number}: {f.error}
                  </li>
                ))}
              </ul>
              <p>
                That invoice is still live in the old store’s organisation —
                void it by hand, or the same sale is counted twice.
              </p>
            </div>
          )}
        </div>

        <div className="mtm__footer">
          {targetId && !xeroFailures.length && (
            <button
              type="button"
              className="mtm__btn mtm__btn--ghost"
              onClick={() => {
                setTargetId(null);
                setPreview(null);
                setError(null);
              }}
              disabled={moving}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="mtm__btn"
            onClick={onClose}
            disabled={moving}
          >
            {xeroFailures.length ? "Close" : "Cancel"}
          </button>
          {targetId && !xeroFailures.length && (
            <button
              type="button"
              className="mtm__btn mtm__btn--primary"
              onClick={confirm}
              disabled={loading || moving || !preview || !!blockers.length}
            >
              {moving ? "Moving…" : "Move Transaction"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MoveTransactionModal;
